import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { ReserveFormData, AgreementInfo } from '../models';
import availableTimeService, {
  AvailableTimesResponse,
  formatDateForAPI,
  getAvailableTimesForDate,
} from '../services/availableTimeService';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Modal,
} from 'react-bootstrap';
import './Common.scss';

// メニュー詳細設定の型定義
interface MenuDetails {
  crystalHealing?: {
    sessions: number;
    areas: string[];
  };
  dnaActivation?: {
    type: string;
  };
  ensoficReiki?: {
    sessionType: string;
    healingType: string;
    photo?: File;
    name?: string;
    age?: number;
    address?: string;
    badHabits?: string;
  };
  sacredGeometry?: {
    pattern: string;
  };
}

export default function ReservePage() {
  const navigate = useNavigate();
  const [date, setDate] = useState<Date>(new Date());
  const [formData, setFormData] = useState<
    Omit<ReserveFormData, 'date'> & { time?: string }
  >({
    name: '',
    email: '',
    menu: '',
    time: '',
  });

  // メニュー詳細設定のstate
  const [menuDetails, setMenuDetails] = useState<MenuDetails>({});

  // クリスタルヒーリングオプションのstate
  const [crystalHealingOption, setCrystalHealingOption] = useState(false);

  // 利用可能時間の管理
  const [availableTimesData, setAvailableTimesData] =
    useState<AvailableTimesResponse>({});
  const [currentMonthTimes, setCurrentMonthTimes] = useState<string[]>([]);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);

  // 免責事項・誓約書関連のstate
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [pledgeAccepted, setPledgeAccepted] = useState(false);
  const [disclaimerAcceptedAt, setDisclaimerAcceptedAt] = useState<
    string | null
  >(null);
  const [pledgeAcceptedAt, setPledgeAcceptedAt] = useState<string | null>(null);

  // 免責事項・誓約書のハンドラー関数
  const handleShowDisclaimer = () => {
    setShowDisclaimerModal(true);
  };

  const handleAcceptDisclaimer = () => {
    const now = new Date().toISOString();
    setDisclaimerAccepted(true);
    setDisclaimerAcceptedAt(now);
    setShowDisclaimerModal(false);
  };

  const handleShowPledge = () => {
    setShowPledgeModal(true);
  };

  const handleAcceptPledge = () => {
    const now = new Date().toISOString();
    setPledgeAccepted(true);
    setPledgeAcceptedAt(now);
    setShowPledgeModal(false);
  };

  // 予約内容確認画面への遷移
  const handlePreviewReservation = () => {
    // バリデーション
    if (!formData.name || !formData.email || !formData.menu) {
      alert('予約情報を全て入力してください');
      return;
    }

    // クリスタルヒーリングオプション選択時のセッション回数と施術部位チェック
    if (
      crystalHealingOption &&
      menuDetails.crystalHealing?.sessions &&
      menuDetails.crystalHealing?.sessions !==
        menuDetails.crystalHealing?.areas.length
    ) {
      alert(
        'クリスタルヒーリングの施術部位はセッション回数と同じ数を選択してください'
      );
      return;
    }

    // エンソフィックレイキ遠隔選択時の顔写真必須チェック
    if (
      formData.menu === 'エンソフィックレイキ' &&
      menuDetails.ensoficReiki?.sessionType?.startsWith('遠隔') &&
      !menuDetails.ensoficReiki?.photo
    ) {
      alert('遠隔ヒーリングには顔写真のアップロードが必要です');
      return;
    }

    // 日時の検証
    if (!date || !formData.time) {
      alert('予約日時を選択してください');
      return;
    }

    // 同意情報を作成
    const agreementInfo: AgreementInfo = {
      disclaimerAccepted: disclaimerAccepted,
      disclaimerAcceptedAt: disclaimerAcceptedAt || '',
      pledgeAccepted: pledgeAccepted,
      pledgeAcceptedAt: pledgeAcceptedAt || '',
      disclaimerVersion: '1.0',
      pledgeVersion: '1.0',
      userAgent: navigator.userAgent,
      // IPアドレスは通常フロントエンドからは取得できないため、
      // バックエンドで取得することが一般的
    };

    const reserveData = {
      name: formData.name,
      email: formData.email,
      date,
      time: formData.time,
      menu: formData.menu,
      menuDetails,
      totalPrice: getMenuPrice(formData.menu),
      agreements: agreementInfo,
    };

    navigate('/reserve-preview', { state: reserveData });
  };

  // 利用可能時間データを取得する関数
  const fetchAvailableTimes = async (currentDate?: Date) => {
    setIsLoadingTimes(true);
    try {
      const data = await availableTimeService.getAvailableTimes(
        currentDate?.getFullYear() || date.getFullYear(),
        (currentDate?.getMonth() || date.getMonth()) + 1
      );
      setAvailableTimesData(data);

      // 現在選択されている日付の利用可能時間を更新
      const selectedDateKey = formatDateForAPI(date);
      const timesForSelectedDate = getAvailableTimesForDate(
        data,
        selectedDateKey
      );
      setCurrentMonthTimes(timesForSelectedDate);
    } catch (error) {
      console.error('利用可能時間の取得に失敗しました:', error);
      setCurrentMonthTimes([]);
    } finally {
      setIsLoadingTimes(false);
    }
  };

  // 初回ロード時に利用可能時間を取得
  useEffect(() => {
    fetchAvailableTimes();
  }, []);

  // 日付が変更された時に利用可能時間を更新
  useEffect(() => {
    const selectedDateKey = formatDateForAPI(date);
    const timesForSelectedDate = getAvailableTimesForDate(
      availableTimesData,
      selectedDateKey
    );
    setCurrentMonthTimes(timesForSelectedDate);
  }, [date, availableTimesData]);

  // カレンダーのタイル内容をカスタマイズ（○×表示）
  const tileContent = ({
    date: tileDate,
    view,
  }: {
    date: Date;
    view: string;
  }) => {
    if (view === 'month') {
      const dateKey = formatDateForAPI(tileDate);
      const timesForDate = getAvailableTimesForDate(
        availableTimesData,
        dateKey
      );

      if (timesForDate.length > 0) {
        return <div style={{ color: 'green', fontWeight: 'bold' }}>○</div>;
      } else {
        return <div style={{ color: 'red', fontWeight: 'bold' }}>×</div>;
      }
    }
    return null;
  };
  const getMenuPrice = (menu: string): number => {
    const basePrice = getBasePrice(menu);
    let totalPrice = basePrice;

    // クリスタルヒーリングオプションの価格計算
    if (crystalHealingOption && menuDetails.crystalHealing) {
      totalPrice += 1500 * menuDetails.crystalHealing.sessions;
    }

    return totalPrice;
  };

  const getBasePrice = (menu: string): number => {
    switch (menu) {
      case 'DNAアクティベーション':
        return 15000;
      case 'エンソフィックレイキ':
        return 7000;
      case '神聖幾何学':
        return 17000;
      default:
        return 0;
    }
  };

  // クリスタルヒーリング詳細設定の表示コンポーネント
  const renderCrystalHealingDetails = () => {
    return (
      <Card className="mb-3 border-warning">
        <Card.Header className="bg-warning text-dark">
          <h6 className="mb-0">クリスタルヒーリング オプション詳細設定</h6>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>セッション回数</Form.Label>
            <Form.Select
              value={menuDetails.crystalHealing?.sessions || 1}
              onChange={e =>
                setMenuDetails(prev => ({
                  ...prev,
                  crystalHealing: {
                    ...prev.crystalHealing,
                    sessions: parseInt(e.target.value),
                    areas: prev.crystalHealing?.areas || [],
                  },
                }))
              }
            >
              <option value={1}>1箇所（15分/¥1,500）</option>
              <option value={2}>2箇所（30分/¥3,000）</option>
              <option value={3}>3箇所（45分/¥4,500）</option>
              <option value={4}>4箇所（60分/¥6,000）</option>
              <option value={5}>5箇所（75分/¥7,500）</option>
              <option value={6}>6箇所（90分/¥9,000）</option>
              <option value={7}>7箇所（105分/¥10,500）</option>
              <option value={8}>8箇所（120分/¥12,000）</option>
              <option value={9}>9箇所（135分/¥13,500）</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>施術部位（複数選択可）</Form.Label>
            {[
              '感情',
              'マインド',
              '肉体',
              '内部の問題',
              '次元空間の反転',
              '時間の強化',
              '純粋なマインド',
              'リビドー',
              'エンパワーメント',
            ].map(area => (
              <Form.Check
                key={area}
                type="checkbox"
                label={area}
                checked={
                  menuDetails.crystalHealing?.areas?.includes(area) || false
                }
                onChange={e => {
                  const currentAreas = menuDetails.crystalHealing?.areas || [];
                  let newAreas: string[];
                  if (e.target.checked) {
                    newAreas = [...currentAreas, area];
                  } else {
                    newAreas = currentAreas.filter(a => a !== area);
                  }
                  setMenuDetails(prev => ({
                    ...prev,
                    crystalHealing: {
                      ...prev.crystalHealing,
                      sessions: prev.crystalHealing?.sessions || 1,
                      areas: newAreas,
                    },
                  }));
                }}
              />
            ))}
          </Form.Group>
        </Card.Body>
      </Card>
    );
  };

  // メニュー詳細の表示コンポーネント
  const renderMenuDetails = () => {
    switch (formData.menu) {
      case 'DNAアクティベーション':
        return (
          <Card className="mb-3 border-success">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">DNAアクティベーション 詳細設定</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>アクティベーション種類</Form.Label>
                <Form.Select
                  value={menuDetails.dnaActivation?.type || ''}
                  onChange={e =>
                    setMenuDetails(prev => ({
                      ...prev,
                      dnaActivation: {
                        type: e.target.value,
                      },
                    }))
                  }
                >
                  <option value="">選択してください</option>
                  <option value="フルセッション">
                    フルセッション（60分/¥15,000）
                  </option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        );

      case 'エンソフィックレイキ':
        return (
          <Card className="mb-3 border-success">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">エンソフィックレイキヒーリング 詳細設定</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>セッション種類</Form.Label>
                <Form.Select
                  value={menuDetails.ensoficReiki?.sessionType || ''}
                  onChange={e =>
                    setMenuDetails(prev => ({
                      ...prev,
                      ensoficReiki: {
                        ...prev.ensoficReiki,
                        sessionType: e.target.value,
                        healingType:
                          prev.ensoficReiki?.healingType || '肉体的な不調',
                      },
                    }))
                  }
                >
                  {/* サロンがないため今は遠隔のみ */}
                  <option value="">選択してください</option>
                  <option value="対面">対面（60分）¥7,000</option>
                  <option value="遠隔">遠隔（60分）¥7,000</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>ヒーリング種類</Form.Label>
                <Form.Select
                  value={
                    menuDetails.ensoficReiki?.healingType || '肉体的な不調'
                  }
                  onChange={e =>
                    setMenuDetails(prev => ({
                      ...prev,
                      ensoficReiki: {
                        ...prev.ensoficReiki,
                        sessionType: prev.ensoficReiki?.sessionType || '',
                        healingType: e.target.value,
                      },
                    }))
                  }
                >
                  <option value="肉体的な不調">肉体的な不調</option>
                  <option value="悪癖の改善">悪癖の改善</option>
                </Form.Select>
              </Form.Group>

              {/* 遠隔選択時表示 */}
              {menuDetails.ensoficReiki?.sessionType?.startsWith('遠隔') && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>顔写真</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // ファイルサイズ制限（5MB）
                          if (file.size > 5 * 1024 * 1024) {
                            alert('ファイルサイズは5MB以下にしてください');
                            return;
                          }

                          // ファイル形式チェック
                          if (!file.type.startsWith('image/')) {
                            alert('画像ファイルを選択してください');
                            return;
                          }

                          setMenuDetails(prev => ({
                            ...prev,
                            ensoficReiki: {
                              ...prev.ensoficReiki,
                              sessionType:
                                prev.ensoficReiki?.sessionType || '遠隔',
                              healingType:
                                prev.ensoficReiki?.healingType ||
                                '肉体的な不調',
                              photo: file,
                            },
                          }));
                        }
                      }}
                    />
                    {menuDetails.ensoficReiki?.photo && (
                      <div className="mt-2 text-success">
                        ✓ 選択されたファイル:{' '}
                        {menuDetails.ensoficReiki.photo.name}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>お名前（フルネーム）</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="山田 太郎"
                      value={menuDetails.ensoficReiki?.name || ''}
                      onChange={e =>
                        setMenuDetails(prev => ({
                          ...prev,
                          ensoficReiki: {
                            ...prev.ensoficReiki,
                            sessionType: prev.ensoficReiki?.sessionType || '',
                            healingType:
                              prev.ensoficReiki?.healingType || '肉体的な不調',
                            name: e.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>年齢</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="30"
                      value={menuDetails.ensoficReiki?.age || ''}
                      onChange={e =>
                        setMenuDetails(prev => ({
                          ...prev,
                          ensoficReiki: {
                            ...prev.ensoficReiki,
                            sessionType: prev.ensoficReiki?.sessionType || '',
                            healingType:
                              prev.ensoficReiki?.healingType || '肉体的な不調',
                            age: parseInt(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>住所</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="東京都渋谷区..."
                      value={menuDetails.ensoficReiki?.address || ''}
                      onChange={e =>
                        setMenuDetails(prev => ({
                          ...prev,
                          ensoficReiki: {
                            ...prev.ensoficReiki,
                            sessionType: prev.ensoficReiki?.sessionType || '',
                            healingType:
                              prev.ensoficReiki?.healingType || '肉体的な不調',
                            address: e.target.value,
                          },
                        }))
                      }
                    />
                  </Form.Group>
                </>
              )}

              {/* 悪癖の改善選択時の詳細項目 */}
              {menuDetails.ensoficReiki?.healingType === '悪癖の改善' && (
                <Form.Group className="mb-3">
                  <Form.Label>改善したい悪癖</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="喫煙、過食、夜更かしなど具体的にお書きください"
                    value={menuDetails.ensoficReiki?.badHabits || ''}
                    onChange={e =>
                      setMenuDetails(prev => ({
                        ...prev,
                        ensoficReiki: {
                          ...prev.ensoficReiki,
                          sessionType: prev.ensoficReiki?.sessionType || '',
                          healingType:
                            prev.ensoficReiki?.healingType || '悪癖の改善',
                          badHabits: e.target.value,
                        },
                      }))
                    }
                  />
                </Form.Group>
              )}
            </Card.Body>
          </Card>
        );

      case '神聖幾何学':
        return (
          <Card className="mb-3 border-success">
            <Card.Header className="bg-success text-white">
              <h6 className="mb-0">神聖幾何学ヒーリング 詳細設定</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>パターン選択</Form.Label>
                <Form.Select
                  value={menuDetails.sacredGeometry?.pattern || ''}
                  onChange={e =>
                    setMenuDetails(prev => ({
                      ...prev,
                      sacredGeometry: {
                        ...prev.sacredGeometry,
                        pattern: e.target.value,
                      },
                    }))
                  }
                >
                  <option value="">選択してください</option>
                  <option value="フルセッション">
                    フルセッション（60分/¥17,000）
                  </option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Container fluid>
      <h2 className="mb-4 text-center text-success white-stroke">予約</h2>
      <h2 className="text-danger text-center">
        現在サロンはありませんので対面セッションは岐阜県西濃地域限定で出張セッションを行い、遠隔セッションはGoogle
        Meetで行います。
      </h2>
      <Row className="justify-content-center">
        <Col lg={8} md={8} sm={10} xs={12} className="mb-4">
          <Form>
            <Form.Group className="mb-3" controlId="contactName">
              <Form.Label className="text-success white-stroke">
                お名前
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="お名前"
                onChange={e =>
                  setFormData(f => ({ ...f, name: e.target.value }))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="contactEmail">
              <Form.Label className="text-success white-stroke">
                メール
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="メール"
                onChange={e =>
                  setFormData(f => ({ ...f, email: e.target.value }))
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-success white-stroke">
                ご希望日
              </Form.Label>
              <Card className="shadow-sm">
                <Card.Body className="d-flex justify-content-center p-3">
                  <Calendar
                    onChange={value => setDate(value as Date)}
                    value={date}
                    className="border-0"
                    tileContent={tileContent}
                    minDate={new Date()} // 過去の日付を選択不可に
                    onActiveStartDateChange={({ activeStartDate }) => {
                      // 月が変更されたときに利用可能時間を再取得
                      if (activeStartDate) {
                        fetchAvailableTimes(activeStartDate);
                      }
                    }}
                  />
                </Card.Body>
              </Card>
            </Form.Group>

            <Form.Group className="mb-3" controlId="menuSelect">
              <Form.Label className="text-success white-stroke">
                ヒーリングメニュー
              </Form.Label>
              <Form.Select
                value={formData.menu}
                onChange={e => {
                  const newMenu = e.target.value;
                  const oldMenu = formData.menu;

                  setFormData(f => ({ ...f, menu: newMenu }));

                  // メニューが実際に変更された場合のみ詳細設定をリセット
                  if (newMenu !== oldMenu) {
                    setMenuDetails({});
                  }
                }}
              >
                <option value="">メニューを選択してください</option>
                <option value="DNAアクティベーション">
                  DNAアクティベーション（¥15,000）
                </option>
                <option value="エンソフィックレイキ">
                  エンソフィックレイキヒーリング（¥7,000）
                </option>
                <option value="神聖幾何学">
                  神聖幾何学ヒーリング（¥17,000）
                </option>
              </Form.Select>
            </Form.Group>

            {/* クリスタルヒーリングオプション */}
            {formData.menu && formData.menu !== '' && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="crystalHealingOption"
                  label="クリスタルヒーリングをオプションとして追加（¥1,500 * セッション数）"
                  checked={crystalHealingOption}
                  onChange={e => {
                    setCrystalHealingOption(e.target.checked);
                    // チェックを外した場合は詳細設定もクリア
                    if (!e.target.checked) {
                      setMenuDetails(prev => ({
                        ...prev,
                        crystalHealing: undefined,
                      }));
                    } else {
                      // チェックを入れた場合はデフォルト値を設定
                      setMenuDetails(prev => ({
                        ...prev,
                        crystalHealing: {
                          sessions: 1,
                          areas: [],
                        },
                      }));
                    }
                  }}
                />
              </Form.Group>
            )}

            {/* メニュー詳細設定 */}
            {formData.menu && renderMenuDetails()}

            {/* クリスタルヒーリング詳細設定（オプション選択時） */}
            {crystalHealingOption && renderCrystalHealingDetails()}

            <Form.Group className="mb-3" controlId="timeSelect">
              <Form.Label className="text-success white-stroke">
                ご希望時間
              </Form.Label>
              <Form.Select
                value={formData.time || ''}
                onChange={e =>
                  setFormData(f => ({ ...f, time: e.target.value }))
                }
                disabled={currentMonthTimes.length === 0 || isLoadingTimes}
              >
                <option value="">
                  {isLoadingTimes
                    ? '時間を読み込み中...'
                    : currentMonthTimes.length === 0
                      ? 'この日は予約できません'
                      : '時間を選択してください'}
                </option>
                {currentMonthTimes.map((time: string) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="text-center">
              {!disclaimerAccepted ? (
                <Button
                  variant="warning"
                  size="lg"
                  onClick={handleShowDisclaimer}
                  className="px-5"
                >
                  免責事項を読む
                </Button>
              ) : !pledgeAccepted ? (
                <Button
                  variant="info"
                  size="lg"
                  onClick={handleShowPledge}
                  className="px-5"
                >
                  誓約書を読む
                </Button>
              ) : (
                <Button
                  variant="success"
                  size="lg"
                  onClick={handlePreviewReservation}
                  className="px-5"
                >
                  予約内容を確認する
                </Button>
              )}
            </div>
          </Form>
        </Col>
      </Row>

      {/* 免責事項ダイアログ */}
      <Modal
        show={showDisclaimerModal}
        onHide={() => setShowDisclaimerModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>免責事項</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ lineHeight: '1.8', fontSize: '14px' }}>
            <h5>重要な免責事項について</h5>
            <p>
              当サロンで提供するヒーリングサービスについて、以下の点を十分にご理解いただいた上でご利用ください。
            </p>

            <h6>1. ヒーリングの性質について</h6>
            <p>
              ヒーリングは、人間が本来持つ自然治癒能力をサポートし、潜在能力の発現を促進することを目的としたものです。
              これは医療行為ではなく、リラクゼーション・健康維持・精神的な成長をサポートするものです。
            </p>

            <h6>2. 医療行為との違い</h6>
            <p>
              ヒーリングは、肉体的な疾患や現在治療中の病気を直接的に治療・治癒するものではありません。
              医師による診断・治療に代わるものでもありません。
              現在医療機関で治療を受けている方は、必ず医師にご相談の上でご利用ください。
            </p>

            <h6>3. 効果について</h6>
            <p>
              潜在能力の解放や自然治癒能力の向上が期待されますが、その効果には個人差があります。
              また、人生の変化や改善については、お客様ご自身の意識や行動によって左右されるものです。
              当サロンは特定の結果をお約束するものではありません。
            </p>

            <h6>4. 責任の限界</h6>
            <p>
              当サロンは、サービス提供に際して最善を尽くしますが、
              セッション中または後に生じる一切の身体的・精神的・経済的損害について責任を負いません。
              お客様ご自身の責任と判断でサービスをご利用ください。
            </p>

            <h6>5. 緊急時の対応</h6>
            <p>
              セッション中に体調不良等が発生した場合は、直ちにお申し出ください。
              必要に応じて医療機関の受診をお勧めします。
            </p>

            <h6>6. 予約のキャンセルについて</h6>
            <p>
              当サロンではイタズラ防止のために、予約確定後のセッション予定日直前のキャンセルは原則としてお受けしておりません。
              ご予約のキャンセルは、セッション予定日の24時間前までにご連絡ください。
              上記期限を過ぎますとメールに添付したリンク先のフォームからのキャンセルはできませんので当日キャンセルなどは必ずメールでご連絡ください。
              当日キャンセルについては、正当な事情がない限り料金の全額を請求させていただきます。
            </p>

            <h6>7. 個人情報の取り扱い</h6>
            <p>
              ご提供いただいた個人情報は、当サロンの
              <span
                style={{
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => window.open('/privacy-policy', '_blank')}
              >
                プライバシーポリシー
              </span>
              に基づき適切に管理・保護されます。
              セッションの提供および関連する連絡以外の目的で使用することはありません。
            </p>

            <h6>8. 対面セッションについて</h6>
            <p>
              対面セッションをご希望の場合、当サロンは現在物理的なサロンを持っておりません。
              エンソフィックレイキヒーリングの遠隔セッション以外の対面セッションは、岐阜県西濃地域にお住まいの方に限り、出張セッションを提供しております。
            </p>

            <p className="mt-3">
              <strong>
                上記の内容を十分にご理解いただき、同意いただける場合のみ「同意する」ボタンを押してください。
              </strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDisclaimerModal(false)}
          >
            同意しない
          </Button>
          <Button variant="success" onClick={handleAcceptDisclaimer}>
            同意する
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 誓約書ダイアログ */}
      <Modal
        show={showPledgeModal}
        onHide={() => setShowPledgeModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>誓約書</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ lineHeight: '1.8', fontSize: '14px' }}>
            <h5>エーテル体ヒーリングセッション受講に関する誓約書</h5>
            <p>
              安全で効果的なセッションを提供するため、以下についてご確認ください。
            </p>

            <h6>施術制限について</h6>
            <p>
              <strong>
                過去3週間以内に、以下のMMSが提供するエーテル体を開くヒーリングセッションを受けていないことをお誓いください：
              </strong>
            </p>

            <ul>
              <li>DNAアクティベーション</li>
              <li>アメガワンドによるDNAアクティベーション</li>
              <li>フルスピリットアクティベーション</li>
              <li>キングソロモンヒーリングモダリティシリーズ</li>
            </ul>

            <h6>理由</h6>
            <p>
              これらのセッションは非常に強力なエネルギー調整を行うため、
              短期間での連続受講は身体・精神への負荷が大きく、
              適切な統合期間を設けることが必要です。
            </p>

            <h6>お客様の責任</h6>
            <p>
              上記セッションの受講歴について正確にお申し出いただくことは、
              お客様の安全と適切なセッション効果のために重要です。
              虚偽の申告による不具合については、当サロンは一切の責任を負いません。
            </p>

            <p className="mt-3">
              <strong>
                過去3週間以内に上記セッションを受けていないことを誓約し、
                同意いただける場合は「誓約・同意する」ボタンを押してください。
              </strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPledgeModal(false)}>
            同意しない
          </Button>
          <Button variant="success" onClick={handleAcceptPledge}>
            誓約・同意する
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
